import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const RefDataContext = createContext({});

export const RefDataProvider = ({ children }) => {
  const [data, setData] = useState({
    priorities: [],
    statuses: [],
    issue_statuses: [],
    asset_types: [],
    service_types: [],
    indian_states: [],
    projects: [],
    biz_projects: [],
    biz_projects_open: [],
    teams: [],
    users: [],
    assets: [],
    services: [],
    tasks: [],
  });

  const refresh = useCallback(async () => {
    try {
      const [opts, projects, bizProjects, teams, users, assets, services, tasks] = await Promise.all([
        api.get("/reference/options"),
        api.get("/projects"),
        api.get("/biz-projects"),
        api.get("/teams"),
        api.get("/users"),
        api.get("/assets"),
        api.get("/services"),
        api.get("/tasks"),
      ]);
      setData({
        priorities: opts.data.priorities,
        statuses: opts.data.statuses,
        issue_statuses: opts.data.issue_statuses || opts.data.statuses || [],
        asset_types: opts.data.asset_types,
        service_types: opts.data.service_types,
        indian_states: opts.data.indian_states || [],
        projects: projects.data.map((p) => ({ value: p.id, label: `${p.code} · ${p.client_name || p.name}` })),
        biz_projects: bizProjects.data.map((p) => ({ value: p.id, label: `${p.code} · ${p.project_name || p.customer_name}` })),
        biz_projects_open: bizProjects.data
          .filter((p) => p.project_status !== "Completed")
          .map((p) => ({ value: p.id, label: `${p.code} · ${p.project_name || p.customer_name}` })),
        teams: teams.data
          .map((t) => ({
            value: t.id,
            label: t.name,
            members: t.members || [],
            team_lead_id: t.team_lead_id || null,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
        users: users.data.map((u) => ({ value: u.id, label: `${u.name} (${u.email})`, name: u.name })),
        assets: assets.data.map((a) => ({ value: a.id, label: `${a.code} · ${a.asset_type || ""}`, project_id: a.project_id || null })),
        services: services.data.map((s) => ({ value: s.id, label: `${s.code} · ${s.service_type}`, project_id: s.project_id || null })),
        tasks: tasks.data.map((t) => ({ value: t.id, label: `${t.code} · ${t.subject}` })),
      });
    } catch (_) {
      // tolerate missing refs (e.g. before login)
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <RefDataContext.Provider value={{ ...data, refresh }}>
      {children}
    </RefDataContext.Provider>
  );
};

export const useRefData = () => useContext(RefDataContext);
